import * as TabsPrimitive from "@radix-ui/react-tabs";
import type { ComponentPropsWithRef } from "react";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

/** TabsList height per size. */
const listSizeStyles = {
  xs: "h-7",
  sm: "h-8",
  default: "h-9",
} as const;

/** Base text size for triggers and panel content (Radix Tabs). */
const tabsTextSizeStyles = {
  xs: "text-xxs",
  sm: "text-xs",
  default: "text-sm",
} as const;

export type TabsSize = keyof typeof listSizeStyles;

interface TabsListProps
  extends ComponentPropsWithRef<typeof TabsPrimitive.List> {
  size?: TabsSize;
}

const TabsList = ({
  className,
  ref,
  size = "default",
  ...props
}: TabsListProps) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Layout and track background
      "inline-flex items-center justify-center rounded-lg bg-muted p-1",
      "text-muted-foreground",
      listSizeStyles[size],
      className,
    )}
    {...props}
  />
);
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends ComponentPropsWithRef<typeof TabsPrimitive.Trigger> {
  size?: TabsSize;
}

const TabsTrigger = ({
  className,
  ref,
  size = "default",
  ...props
}: TabsTriggerProps) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base layout and typography
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 font-normal",
      "ring-offset-background transition-all",
      // Focus ring
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      // Disabled
      "disabled:pointer-events-none disabled:opacity-50",
      // Active tab
      "data-[state=active]:bg-white data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow",
      tabsTextSizeStyles[size],
      className,
    )}
    {...props}
  />
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps
  extends ComponentPropsWithRef<typeof TabsPrimitive.Content> {
  size?: TabsSize;
}

const TabsContent = ({
  className,
  ref,
  size = "default",
  ...props
}: TabsContentProps) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // Spacing and ring offset
      "mt-2 ring-offset-background",
      // `forceMount` keeps panels mounted but Radix clears `hidden`; hide inactive via `data-state`.
      "data-[state=inactive]:hidden",
      // Focus ring
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      tabsTextSizeStyles[size],
      className,
    )}
    {...props}
  />
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
